import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Loader2, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MoltbookIntegrationPage = () => {
  const [moltbookInfo, setMoltbookInfo] = useState(null);
  const [testResponse, setTestResponse] = useState(null);
  const [testChannel, setTestChannel] = useState('instagram');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const response = await axios.get(`${API}/moltbook/info`);
        setMoltbookInfo(response.data);
      } catch (error) {
        { /* Fetch fehlgeschlagen – kein kritischer Fehler */ }
      }
    };
    fetchInfo();
  }, []);

  const handleTestEndpoint = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/moltbook/content`, { channel: testChannel });
      setTestResponse(response.data);
      toast.success('Moltbook-Endpoint erfolgreich getestet!');
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="moltbook-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-stone-900 flex items-center gap-3">
            <Zap className="w-8 h-8 text-fox-fire" />
            Moltbook Integration
          </h1>
          <p className="text-stone-500 mt-1">API-Endpoint für Moltbook-Agents</p>
        </div>
      </div>

      {/* Endpoint Info Card */}
      <div className="bg-gradient-to-br from-fox-fire/10 via-orange-50 to-amber-50 rounded-xl border border-orange-200 p-6">
        <h2 className="font-heading text-xl font-bold mb-4">🔗 Moltbook Content Endpoint</h2>

        <div className="bg-white rounded-lg p-4 font-mono text-sm mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-bold">POST</span>
            <span className="text-stone-800">{backendUrl}/api/moltbook/content</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4">
            <h3 className="font-bold text-stone-900 mb-2">📤 Request Body</h3>
            <pre className="bg-stone-50 p-3 rounded text-xs overflow-x-auto">
{`{
  "channel": "instagram",
  "topic": "Bewerbungscoaching" // optional
}`}
            </pre>
          </div>

          <div className="bg-white rounded-lg p-4">
            <h3 className="font-bold text-stone-900 mb-2">📥 Response</h3>
            <pre className="bg-stone-50 p-3 rounded text-xs overflow-x-auto">
{`{
  "channel": "instagram",
  "title": "Instagram: ...",
  "text": "🚀 Dein Traumjob...",
  "hashtags": ["#Förderfuchs"],
  "image_idea": "...",
  "call_to_action": "..."
}`}
            </pre>
          </div>
        </div>
      </div>

      {/* Test Endpoint */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="font-heading text-lg font-bold mb-4">🧪 Endpoint testen</h2>

        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-stone-700 mb-2">Kanal</label>
            <select
              value={testChannel}
              onChange={(e) => setTestChannel(e.target.value)}
              className="w-full p-3 border border-stone-200 rounded-lg"
            >
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="tiktok">TikTok</option>
              <option value="linkedin">LinkedIn</option>
              <option value="blog">Blog</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="moltbook">Moltbook</option>
            </select>
          </div>
          <button
            onClick={handleTestEndpoint}
            disabled={loading}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play size={18} />}
            Testen
          </button>
        </div>

        {testResponse && (
          <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <h3 className="font-bold text-green-800 mb-2">✅ Erfolgreiche Response:</h3>
            <pre className="bg-white p-3 rounded text-xs overflow-x-auto max-h-60">
              {JSON.stringify(testResponse.moltbook_response, null, 2)}
            </pre>
          </div>
        )}
      </div>

      {/* Usage Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h2 className="font-heading text-lg font-bold text-blue-900 mb-4">📖 So nutzt ein Moltbook-Agent diesen Endpoint</h2>

        <ol className="space-y-3 text-blue-800">
          {[
            ['Moltbook-Agent sendet POST-Request an', '/api/moltbook/content'],
            ['Agent wählt Kanal im Request Body (z.B.', '"channel": "instagram"'],
            ['API generiert KI-Content speziell für den gewählten Kanal', null],
            ['Agent erhält strukturiertes JSON mit Text, Hashtags, Bild-Idee, CTA', null],
            ['Agent kann Content weiterverarbeiten oder direkt posten', null],
          ].map(([text, code], i) => (
            <li key={i} className="flex gap-3">
              <span className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">{i + 1}</span>
              <span>
                {text}
                {code && <code className="bg-blue-100 px-1 rounded ml-1">{code}</code>}
                {i === 0 ? ')' : i === 1 ? ')' : ''}
              </span>
            </li>
          ))}
        </ol>

        <div className="mt-4 p-3 bg-blue-100 rounded-lg">
          <strong>💡 Tipp:</strong> Rufe den Endpoint täglich um 10:00 Uhr auf für frischen Förderfuchs-Content!
        </div>
      </div>

      {/* Available Channels */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <h2 className="font-heading text-lg font-bold mb-4">Verfügbare Kanäle</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {['instagram', 'facebook', 'tiktok', 'linkedin', 'blog', 'whatsapp', 'moltbook'].map(channel => (
            <div key={channel} className="p-3 bg-stone-50 rounded-lg text-center">
              <code className="text-xs font-mono text-fox-fire">{channel}</code>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MoltbookIntegrationPage;
